import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Quota {
  id: string
  sales_rep: string
  period: string
  quota_amount: number
  achieved: number
  attainment: number
  gap_to_quota: number
  pace: string
  forecast_attainment: number
}

interface Territory {
  id: string
  name: string
  total_quota: number
  achieved: number
  attainment: number
  rep_count: number
  avg_rep_attainment: number
  growth_yoy: number
}

interface QuotaTrend {
  id: string
  period: string
  total_quota: number
  total_achieved: number
  attainment: number
  reps_at_quota: number
  reps_below_quota: number
  reps_above_quota: number
}

interface Statistics {
  total_quota: number
  total_achieved: number
  overall_attainment: number
  reps_at_or_above_quota: number
  avg_quota_per_rep: number
  quota_accuracy: number
}

export default function QuotaManagementPage() {
  const [quotas, setQuotas] = useState<Quota[]>([])
  const [territories, setTerritories] = useState<Territory[]>([])
  const [trends, setTrends] = useState<QuotaTrend[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [quotasRes, territoriesRes, trendsRes, statsRes] = await Promise.all([
        fetch('/api/quotamanagement/quotas'),
        fetch('/api/quotamanagement/territories'),
        fetch('/api/quotamanagement/trends'),
        fetch('/api/quotamanagement/statistics')
      ])
      if (quotasRes.ok) setQuotas(await quotasRes.json())
      if (territoriesRes.ok) setTerritories(await territoriesRes.json())
      if (trendsRes.ok) setTrends(await trendsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching quota management data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const getPaceBadge = (pace: string) => {
    if (pace === 'ahead') return 'bg-green-100 text-green-800'
    if (pace === 'behind') return 'bg-red-100 text-red-800'
    return 'bg-yellow-100 text-yellow-800'
  }

  const getAttainmentColor = (attainment: number) => {
    if (attainment >= 100) return 'text-green-600'
    if (attainment >= 80) return 'text-yellow-600'
    return 'text-red-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Quota Management</h1>
          <p className="mt-2 text-gray-600">Phase 236 - Quotas, attainment, and territories</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Quota</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(statistics.total_quota)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Achieved</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.total_achieved)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Attainment</p>
              <p className={`text-2xl font-bold ${getAttainmentColor(statistics.overall_attainment)}`}>{statistics.overall_attainment}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">At/Above Quota</p>
              <p className="text-2xl font-bold text-green-600">{statistics.reps_at_or_above_quota}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Quota/Rep</p>
              <p className="text-2xl font-bold text-purple-600">{formatCurrency(statistics.avg_quota_per_rep)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Accuracy</p>
              <p className="text-2xl font-bold text-indigo-600">{statistics.quota_accuracy}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Rep Quota Attainment</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rep</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quota</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Achieved</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attainment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pace</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Forecast</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {quotas.map((quota) => (
                  <tr key={quota.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{quota.sales_rep}</td>
                    <td className="px-6 py-4 text-gray-500">{quota.period}</td>
                    <td className="px-6 py-4 text-gray-500">{formatCurrency(quota.quota_amount)}</td>
                    <td className="px-6 py-4 text-blue-600">{formatCurrency(quota.achieved)}</td>
                    <td className={`px-6 py-4 font-bold ${getAttainmentColor(quota.attainment)}`}>{quota.attainment}%</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getPaceBadge(quota.pace)}`}>{quota.pace}</span>
                    </td>
                    <td className="px-6 py-4 text-purple-600">{quota.forecast_attainment}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Territory Performance</h2>
            </div>
            <div className="p-6 space-y-4">
              {territories.map((territory) => (
                <div key={territory.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{territory.name}</span>
                    <span className={`text-lg font-bold ${getAttainmentColor(territory.attainment)}`}>{territory.attainment}%</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 mb-2">
                    <span>Quota: {formatCurrency(territory.total_quota)}</span>
                    <span>Achieved: {formatCurrency(territory.achieved)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Reps</p>
                      <p className="font-medium">{territory.rep_count}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Avg Attain</p>
                      <p className={`font-medium ${getAttainmentColor(territory.avg_rep_attainment)}`}>{territory.avg_rep_attainment}%</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">YoY Growth</p>
                      <p className={`font-medium ${territory.growth_yoy >= 0 ? 'text-green-600' : 'text-red-600'}`}>{territory.growth_yoy}%</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Quota Trends</h2>
            </div>
            <div className="p-6 space-y-4">
              {trends.map((trend) => (
                <div key={trend.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{trend.period}</span>
                    <span className={`text-lg font-bold ${getAttainmentColor(trend.attainment)}`}>{trend.attainment}%</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 mb-2">
                    <span>Quota: {formatCurrency(trend.total_quota)}</span>
                    <span>Achieved: {formatCurrency(trend.total_achieved)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Above</p>
                      <p className="font-medium text-green-600">{trend.reps_above_quota}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">At Quota</p>
                      <p className="font-medium text-blue-600">{trend.reps_at_quota}</p>
                    </div>
                    <div className="text-center p-2 bg-white rounded">
                      <p className="text-gray-500">Below</p>
                      <p className="font-medium text-red-600">{trend.reps_below_quota}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
